/**
 * 🚀 [Phase v13.50 — 2026-07-14] Helper pur — Enrichissement des BT avec shadows approximatifs (Sujet 4.D)
 *
 * Rôle :
 *   Transforme la liste des BT chargée depuis `GET /api/crm/bons-travail` en
 *   étendant chaque BT ayant une `planification_approx.dates_calculees` peuplée
 *   avec un « shadow » virtuel qui apparaît dans le calendrier de production
 *   avec un rendu visuel distinct (opacity 0.55 + border dashed + lane
 *   spéciale pour les Sans chef).
 *
 * Contraintes métier verrouillées (v13.50) :
 *   1. Chaque BT source garde son rendu officiel intact (`type_planification: 'officielle'`)
 *   2. Un shadow est ajouté par BT avec `dates_calculees.length > 0` (via `planification_approx`)
 *   3. Le shadow d'un chef donné est MASQUÉ un jour où ce chef a une date
 *      officielle (dans N'IMPORTE quel BT) — filtrage global inter-BT.
 *   4. Les shadows Sans chef vont dans la lane virtuelle `__non_assigne__`
 *      (pas de filtrage de chevauchement — 2 BT sans chef le même jour se
 *      chevauchent visuellement, c'est le rappel qu'il faut assigner un chef).
 *
 * Pur JS CommonJS (compatible Jest sans transformer).
 */

const LANE_NON_ASSIGNE = '__non_assigne__';

/**
 * Construit un Set des dates officielles occupées par chef.
 * Utilisé pour le filtrage global inter-BT du chevauchement officiel/approx.
 *
 * @param {Array} bons — liste des BT
 * @returns {Map<string, Set<string>>} chef_id → set des dates officielles (ISO YYYY-MM-DD)
 */
function buildOfficialDatesByChef(bons) {
  const map = new Map();
  for (const bon of bons || []) {
    const chefId = bon.chef_equipe_id;
    if (!chefId) continue;
    // Priorité aux `chef_dates_assignees` (Phase 57-58 : dates explicites du chef),
    // sinon fallback aux `dates_chantier` (chef présent tous les jours par défaut).
    const dates = (bon.chef_dates_assignees && bon.chef_dates_assignees.length > 0)
      ? bon.chef_dates_assignees
      : (bon.dates_chantier || []);
    if (!map.has(chefId)) map.set(chefId, new Set());
    const set = map.get(chefId);
    for (const d of dates) if (d) set.add(d);
  }
  return map;
}

/**
 * Enrichit la liste des BT avec :
 *   - Un marqueur `type_planification: 'officielle'` sur chaque BT source.
 *   - Un shadow `type_planification: 'approximative'` pour chaque BT avec
 *     `planification_approx.dates_calculees` non-vide, en filtrant :
 *       a) les dates approx déjà couvertes par les dates officielles DU MÊME BT
 *       b) les dates approx qui coïncident avec une date officielle du chef
 *          dans N'IMPORTE quel autre BT (filtre global)
 *
 * @param {Array} bons — liste brute des BT
 * @returns {Array} liste enrichie (source + shadows)
 */
function enrichirBtsAvecApprox(bons) {
  if (!Array.isArray(bons)) return [];

  const officialDatesByChef = buildOfficialDatesByChef(bons);
  const resultat = [];

  for (const bon of bons) {
    // 1) Le BT source est toujours poussé avec un marquage explicite.
    resultat.push({ ...bon, type_planification: 'officielle' });

    // 2) Shadow approx si présent.
    const planif = bon.planification_approx;
    if (!planif || !Array.isArray(planif.dates_calculees) || planif.dates_calculees.length === 0) {
      continue;
    }

    const chefApproxId = planif.chef_id || null;
    const laneCible = chefApproxId || LANE_NON_ASSIGNE;

    // Dates officielles du MÊME BT (à exclure du shadow)
    const datesOffMemeBt = new Set(bon.dates_chantier || []);

    // Dates officielles du chef approx dans TOUS les autres BT
    // (filtre global — seulement si un chef est assigné à l'approx).
    const datesOffChefApprox = (chefApproxId && officialDatesByChef.has(chefApproxId))
      ? officialDatesByChef.get(chefApproxId)
      : new Set();

    const datesShadow = planif.dates_calculees.filter(d => {
      if (!d) return false;
      if (datesOffMemeBt.has(d)) return false;
      if (datesOffChefApprox.has(d)) return false;
      return true;
    });

    if (datesShadow.length === 0) continue;

    resultat.push({
      ...bon,
      id: `${bon.id}__approx`,
      bon_officiel_id: bon.id,
      chef_equipe_id: laneCible,
      // Nom du chef affiché en tête de lane (visuel). Pour Sans chef, vide.
      chef_equipe_nom: chefApproxId ? bon.chef_equipe_nom : '',
      dates_chantier: datesShadow,
      chef_dates_assignees: datesShadow,
      // Pas de punchs sur un shadow approx
      punches: [],
      // Marqueur discriminant pour le rendu visuel dans GanttGridLane
      type_planification: 'approximative',
      // Voeu original conservé pour tooltip
      planif_approx_voeu: {
        mois: planif.mois,
        annee: planif.annee,
        portion: planif.portion,
        duree_jours_ouvrables: planif.duree_jours_ouvrables,
      },
    });
  }

  return resultat;
}

/**
 * Ordonne la liste des lanes en injectant `__non_assigne__` en TÊTE si présent.
 *
 * @param {Array<string>} laneOrder — ordre existant des chef_id
 * @param {Array} bonsEnrichis — liste enrichie (pour détecter si la lane doit apparaître)
 * @returns {Array<string>}
 */
function ordonnerLanesAvecNonAssigne(laneOrder, bonsEnrichis) {
  const hasNonAssigne = (bonsEnrichis || []).some(b => b.chef_equipe_id === LANE_NON_ASSIGNE);
  if (!hasNonAssigne) return laneOrder;
  const sansNonAssigne = laneOrder.filter(l => l !== LANE_NON_ASSIGNE);
  return [LANE_NON_ASSIGNE, ...sansNonAssigne];
}

/**
 * Construit le libellé FR-CA du tooltip approx à partir du voeu.
 * Exemple : « Planification approximative — Début juin 2026 — 5 jour(s) ouvrable(s) »
 */
function buildTooltipApproximatif(voeu) {
  if (!voeu) return 'Planification approximative';
  const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                   'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
  const PORTIONS_FR = { debut: 'Début', milieu: 'Milieu', fin: 'Fin' };
  const portionLbl = PORTIONS_FR[voeu.portion] || voeu.portion || '';
  const moisLbl = MOIS_FR[(voeu.mois || 1) - 1] || '';
  const nJours = voeu.duree_jours_ouvrables || 0;
  const suffixe = nJours > 1 ? 's' : '';
  return `Planification approximative — ${portionLbl} ${moisLbl} ${voeu.annee || ''} — ${nJours} jour${suffixe} ouvrable${suffixe}`;
}

module.exports = {
  LANE_NON_ASSIGNE,
  buildOfficialDatesByChef,
  enrichirBtsAvecApprox,
  ordonnerLanesAvecNonAssigne,
  buildTooltipApproximatif,
};
